if (!customElements.get('media-gallery')) {
  const DISCOUNT_SELECTORS = {
    subscriptionRadio: '.selling-plans-alt__option--subscription .selling-plans-alt__radio-input:checked',
    variantIdInput: 'input[name="id"]',
    badge: '[data-product-media-discount-badge]',
    percentEl: '[data-discount-percent]',
  };

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this._variantDiscountMap = this._parseVariantDiscounts(this.dataset.variantDiscounts || '');
        this._subscriptionDiscountMap = this._parseVariantDiscounts(this.dataset.variantDiscountsSubscription || '');
        if (!this.elements.thumbnails) return;

        this._debouncedSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          // Button-triggered navigation already knows its target slide, so update
          // the active thumbnail right away instead of waiting on the debounce
          // that smooths out rapid scroll/swipe-driven slide changes.
          if (event.detail?.immediate) {
            this.onSlideChanged(event);
          } else {
            this._debouncedSlideChanged(event);
          }
        });
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this._boundVariantChange = this._onVariantChange.bind(this);
        this._boundSellingPlanChange = this._onSellingPlanChange.bind(this);
        document.addEventListener('variant:change', this._boundVariantChange);
        document.addEventListener('change', this._boundSellingPlanChange);
        this._syncBadgeToCurrentState();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._boundVariantChange);
        document.removeEventListener('change', this._boundSellingPlanChange);
      }

      _style5Host() {
        const sid = (this.id || '').replace(/^MediaGallery-/, '');
        if (sid) {
          return document.querySelector(`selling-plans-alt.style-5[data-section-id="${sid}"]`);
        }
        return document.querySelector('selling-plans-alt.style-5');
      }

      _onSellingPlanChange(event) {
        if (this._style5Host()) return;
        if (!event.target.closest('.selling-plans-alt')) return;
        if (!event.target.matches('.selling-plans-alt__radio-input')) return;
        this._syncBadgeToCurrentState();
      }

      _syncBadgeToCurrentState() {
        if (this._style5Host()) return;
        const variantId = document.querySelector(DISCOUNT_SELECTORS.variantIdInput)?.value;
        if (variantId) this.updateDiscountBadge(String(variantId));
      }

      _getCurrentDiscountMap() {
        return document.querySelector(DISCOUNT_SELECTORS.subscriptionRadio)
          ? this._subscriptionDiscountMap
          : this._variantDiscountMap;
      }

      _parseVariantDiscounts(str) {
        const map = {};
        if (!str) return map;
        str.split(',').forEach((pair) => {
          const [id, pct] = pair.split(':');
          if (id && pct !== undefined) map[id.trim()] = parseInt(pct.trim(), 10);
        });
        return map;
      }

      _onVariantChange(event) {
        if (this._style5Host()) return;
        if (event.detail?.source === 'selling-plans-alt') return;
        const variantId = event.detail?.variantId || event.detail?.variant?.id;
        if (variantId) this.updateDiscountBadge(String(variantId));
      }

      /**
       * Called by selling-plans-alt after it computes real subscription vs one-time save %.
       * @param {number} percent
       */
      setSavePercent(percent) {
        const badge = this.querySelector(DISCOUNT_SELECTORS.badge);
        if (!badge) return;
        const n = Math.max(0, Math.round(Number(percent) || 0));
        const percentEl = badge.querySelector(DISCOUNT_SELECTORS.percentEl);
        if (percentEl) percentEl.textContent = String(n);
        badge.classList.toggle('product__media-discount-badge--hidden', n <= 0);
      }

      updateDiscountBadge(variantId) {
        if (this._style5Host()) return;
        const badge = this.querySelector(DISCOUNT_SELECTORS.badge);
        if (!badge) return;
        const map = this._getCurrentDiscountMap();
        const percent = map[variantId] ?? 0;
        const percentEl = badge.querySelector(DISCOUNT_SELECTORS.percentEl);
        if (percentEl) percentEl.textContent = percent;
        badge.classList.toggle('product__media-discount-badge--hidden', percent <= 0);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const slider = this.elements.thumbnails.slider;

        if (this.dataset.desktopLayout === 'thumbnail_carousel_custom') {
          // Center the active thumbnail instead of pinning it to the left edge,
          // so earlier thumbnails stay visible too (matches the reference
          // carousel at /pages/masony-2#killAcneSection).
          const scrollTarget = thumbnail.offsetLeft - slider.clientWidth / 2 + thumbnail.clientWidth / 2;
          slider.scrollTo({ left: scrollTarget, behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
